import { ActivityIndicator, StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import { CuePill, loadErrorText, type UiTheme } from "@heraion/ui";

import { useShellProfile } from "./ThemeProfileBridge";

/** A profile read that failed, with its retry (InboxSection's form). */
export function ProfileReadFailed({
  theme,
  error,
  onRetry,
  onClose,
}: {
  theme: UiTheme;
  error: string;
  onRetry: () => void;
  /** A surface the athlete opened and cannot otherwise leave (#343). */
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const actions = [
    { label: t("inbox.retry"), onPress: onRetry },
    ...(onClose ? [{ label: t("mobile.common.close"), onPress: onClose }] : []),
  ];
  return (
    <CuePill
      theme={theme}
      tone="danger"
      icon="warn"
      size="base"
      weight="600"
      live
      label={loadErrorText(t, error)}
      actions={actions}
    />
  );
}

/** What a weight-writing surface shows in its place while the unit is null
 *  (#343): the read running, or the read failed. */
export function ProfileLoadState({ theme, onClose }: { theme: UiTheme; onClose?: () => void }) {
  const { t } = useTranslation();
  const { error, refresh } = useShellProfile();
  return (
    <View
      // The failure's CuePill is a live region itself: one announcement.
      accessibilityLiveRegion={error === null ? "polite" : "none"}
      style={[styles.center, { backgroundColor: theme.palette.bg, padding: theme.spacing.s4 }]}
    >
      {error === null ? (
        <ActivityIndicator
          accessibilityRole="progressbar"
          accessibilityLabel={t("profile.loading")}
          color={theme.palette.ink2}
        />
      ) : (
        <ProfileReadFailed theme={theme} error={error} onRetry={() => void refresh()} onClose={onClose} />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, alignItems: "center", justifyContent: "center" },
});
